/**
 * ScheduledJobs.tsx — Automation / Scheduled Jobs page (Closr style)
 *
 * P1: the accent-tinted hint banner neutralized (edge-panel border, no shadow).
 * Slice A: real wire mapping (scheduleExpr/agent join in dashboard/api.ts),
 * empty state, and the Enable toggle wired to PATCH /v1/schedules/{id}.
 * Slice C: create/edit drawer (the AssetDrawer recipe), cronLabel in the
 * Schedule column, UTC-explicit — the runner evaluates UTC only, so no
 * timezone is collected and time inputs say so.
 */
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Clock, Plus, X } from 'lucide-react'
import { toast } from 'sonner'
import { SectionTitle, StatusBadge, Toggle, LoadingState, ErrorState, EmptyState } from '../DashboardShared'
import { useAsync, fetchSchedules, fetchAgents, createSchedule, updateScheduleExpr, updateScheduleStatus } from '../api'
import { apiErrorMessage } from '../../../api/client'
import { overlay } from '../../../theme/tokens'
import { buildCron, parseCron, cronLabel, WEEKDAYS, type Frequency } from '../../../lib/scheduleCron'
import type { DashSchedule } from '../types'

// Client-side mirror of the server's executability rule (the server stays
// authoritative — its named bad-request surfaces via apiErrorMessage).
function exprGuard(expr: string): string | null {
  if (!expr) return 'An expression is required'
  if (expr === '@hourly' || expr === '@daily') return null
  if (expr.startsWith('@every ')) {
    // Sub-minute floors are server-rejected; catch the plain n[m]s cases here
    // ("90s" is legal — only reject when the value is actually under a minute).
    const m = expr.slice(7).trim().match(/^(\d+(?:\.\d+)?)(ms|s)$/)
    if (m && Number(m[1]) < (m[2] === 's' ? 60 : 60_000)) return 'Minimum interval is 1 minute'
    return null
  }
  if (expr.split(/\s+/).length !== 5) return 'Cron needs 5 fields (min hour dom month dow)'
  return null
}

// ── Create / Edit drawer (the AssetDrawer recipe) ─────────────────────────────

function ScheduleDrawer({ schedule, onClose, onSaved }: {
  schedule: DashSchedule | null // null = create
  onClose: () => void
  onSaved: () => void
}) {
  const parsed = schedule ? parseCron(schedule.expr) : null
  const [agentId, setAgentId] = useState(schedule?.agentId ?? '')
  const [name, setName] = useState(schedule?.name ?? '')
  const [freq, setFreq] = useState<Frequency>(parsed?.freq ?? 'daily')
  const [time, setTime] = useState(parsed?.time ?? '09:00')
  const [weekday, setWeekday] = useState(parsed?.weekday ?? 1)
  const [custom, setCustom] = useState(parsed?.freq === 'custom' && schedule ? schedule.expr : '0 9 * * 1')
  const [showCron, setShowCron] = useState(parsed?.freq === 'custom')
  const [saving, setSaving] = useState(false)

  // Agent picklist = live agents (create only; PATCH carries no agentId).
  const agents = useAsync(fetchAgents, [])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  const expr = freq === 'custom' ? custom.trim() : buildCron({ freq, time, weekday })

  async function handleSave() {
    if (!schedule && !agentId) { toast.error('Pick an agent'); return }
    if (!schedule && !name.trim()) { toast.error('Name is required'); return }
    const guard = exprGuard(expr)
    if (guard) { toast.error(guard); return }
    setSaving(true)
    try {
      if (schedule) {
        await updateScheduleExpr(schedule.id, expr)
        toast.success('Schedule updated')
      } else {
        await createSchedule(agentId, { name: name.trim(), scheduleExpr: expr })
        toast.success('Schedule created')
      }
      onSaved()
      onClose()
    } catch (e) {
      toast.error(apiErrorMessage(e, schedule ? 'Failed to update schedule' : 'Failed to create schedule'))
    } finally {
      setSaving(false)
    }
  }

  // bg-surface-control + ink-body is the PROVEN portal-form recipe
  // (OperationsAssets.tsx AssetDrawer): a portal renders outside .dash-shell,
  // so without an explicit background token the UA paints inputs white and
  // the light ink tokens vanish into them.
  const inputCls = 'w-full px-4 py-3 bg-surface-control border border-edge-control rounded-control text-13 text-ink-body placeholder:text-ink-faint transition-all'
  const labelCls = 'block text-12 font-semibold text-ink-body mb-2'
  const radioCls = 'flex items-center gap-2 text-13 text-ink-body cursor-pointer'

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim, backdropFilter: 'blur(2px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      {/* PORTAL RULE (theme/theme.ts): no colorScheme here. data-theme +
          color-scheme are stamped on <html>, so this portal subtree inherits
          both. */}
      <div className="k-drawer"
        style={{ width: 'min(520px, 100vw)', animation: 'slide-in-right 220ms cubic-bezier(0.23,1,0.32,1)' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0 border-b border-edge-row">
          <div>
            <div className="text-detail-title font-bold text-ink-primary">{schedule ? 'Edit Schedule' : 'New Schedule'}</div>
            <div className="text-12 text-ink-muted mt-1">
              {schedule
                ? 'Change when this agent runs. All times are UTC.'
                : 'Run an agent on a recurring schedule. All times are UTC.'}
            </div>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-control text-ink-faint hover:bg-surface-nav-hover transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">
          {schedule ? (
            // Agent and name are create-time only: PATCH carries neither.
            <div>
              <label className={labelCls}>Schedule</label>
              <div className="text-13 text-ink-primary font-medium">{schedule.name || '(unnamed)'}</div>
              <div className="text-12 text-ink-muted mt-1">Agent: {schedule.agentName}</div>
            </div>
          ) : (
            <>
              <div>
                <label className={labelCls}>Agent<span className="text-danger ml-1">*</span></label>
                <select value={agentId} onChange={e => setAgentId(e.target.value)} className={inputCls} autoFocus>
                  <option value="">Pick an agent…</option>
                  {(agents.data ?? []).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Name<span className="text-danger ml-1">*</span></label>
                <input type="text" value={name} onChange={e => setName(e.target.value)}
                  placeholder="e.g. Nightly market sweep" className={inputCls} />
              </div>
            </>
          )}

          {/* Frequency (the Mission Control picker, drawer-styled) */}
          <div>
            <label className={labelCls}>Frequency</label>
            <div className="flex flex-col gap-3">
              {([
                ['hourly', 'Every hour'],
                ['daily',  'Every day'],
                ['weekly', 'Every week'],
              ] as const).map(([f, label]) => (
                <label key={f} className={radioCls}>
                  <input type="radio" name="sched-freq" checked={freq === f} onChange={() => setFreq(f)} />
                  {label}
                  {f === 'daily' && freq === 'daily' && (
                    <input type="time" value={time} onChange={e => setTime(e.target.value)}
                      className={`${inputCls} max-w-[130px] ml-2 py-2`} aria-label="Time (UTC)" />
                  )}
                  {f === 'weekly' && freq === 'weekly' && (
                    <span className="flex items-center gap-2 ml-2">
                      <select value={weekday} onChange={e => setWeekday(Number(e.target.value))} className={`${inputCls} max-w-[110px] py-2`}>
                        {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                      </select>
                      <input type="time" value={time} onChange={e => setTime(e.target.value)}
                        className={`${inputCls} max-w-[130px] py-2`} aria-label="Time (UTC)" />
                    </span>
                  )}
                  {(f === 'daily' && freq === 'daily') || (f === 'weekly' && freq === 'weekly')
                    ? <span className="text-11 text-ink-faint">UTC</span> : null}
                </label>
              ))}
            </div>
            <div className="mt-3">
              <button type="button"
                onClick={() => { setShowCron(v => !v); if (!showCron) setFreq('custom') }}
                className="text-11 text-ink-faint underline bg-transparent border-none cursor-pointer p-0 hover:text-ink-secondary">
                {showCron ? '− Hide' : '+ Advanced'} cron expression
              </button>
              {showCron && (
                <div className="mt-2 flex items-center gap-3">
                  <label className={radioCls}>
                    <input type="radio" name="sched-freq" checked={freq === 'custom'} onChange={() => setFreq('custom')} />
                    Custom
                  </label>
                  <input value={custom}
                    onChange={e => { setCustom(e.target.value); setFreq('custom') }}
                    placeholder="0 9 * * 1" spellCheck={false}
                    className={`${inputCls} font-mono max-w-[220px] py-2`} />
                  <span className="text-11 text-ink-faint">UTC</span>
                </div>
              )}
            </div>
            <div className="text-12 text-ink-muted mt-3">
              Will run: <span className="font-mono text-ink-secondary">{expr || '—'}</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-edge-row flex-shrink-0">
          <button className="k-btn-secondary" onClick={onClose}>Cancel</button>
          <button className="k-btn" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : schedule ? 'Save changes' : 'Create schedule'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function ScheduledJobs() {
  const [reloadKey, setReloadKey] = useState(0)
  const { data, loading, error } = useAsync(fetchSchedules, [reloadKey])
  const [drawer, setDrawer] = useState<{ open: boolean; schedule: DashSchedule | null }>({ open: false, schedule: null })

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  const schedules = data ?? []
  // Banner only when there is a real upcoming duration ("—" = unseeded, "due" = passed).
  const nextActive = schedules.find(s => s.status === 'active' && s.nextRun !== '—' && s.nextRun !== 'due')

  async function toggle(id: string, on: boolean) {
    try {
      await updateScheduleStatus(id, on ? 'active' : 'disabled')
      setReloadKey(k => k + 1)
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to update schedule'))
    }
  }

  return (
    <div className="page-enter flex flex-col gap-5">
      <div className="flex items-start justify-between">
        <SectionTitle title="Scheduled Jobs" subtitle="All agent schedules and their upcoming run times (UTC)" />
        <button className="k-btn inline-flex items-center gap-2" onClick={() => setDrawer({ open: true, schedule: null })}>
          <Plus size={14} /> New schedule
        </button>
      </div>

      {nextActive && (
        <div className="flex items-center gap-3 bg-surface-card rounded-tile px-4 py-3 border border-edge-panel">
          <Clock size={15} className="text-accent" />
          <span className="text-13 text-ink-secondary font-medium">
            Next run: <strong>{nextActive.agentName}</strong> in <strong>{nextActive.nextRun}</strong>
          </span>
        </div>
      )}

      <div className="k-card">
        {schedules.length === 0 ? (
          <EmptyState
            message="No schedules yet"
            hint="Run an agent on a recurring schedule — hourly, daily, weekly, or custom cron"
            actionLabel="New schedule"
            onAction={() => setDrawer({ open: true, schedule: null })}
          />
        ) : (
          <table className="closr-table dash-table w-full">
            <thead>
              <tr>
                <th className="text-left">Agent</th>
                <th className="text-left">Schedule</th>
                <th className="text-left">Next Run In</th>
                <th className="text-left">Last Run</th>
                <th className="text-left">Status</th>
                <th className="text-right">Enable</th>
              </tr>
            </thead>
            <tbody>
              {schedules.map(s => (
                <tr key={s.id}>
                  <td className="font-medium text-ink-primary">{s.agentName}</td>
                  <td>
                    <div className="text-13 text-ink-body">{cronLabel(s.expr)}</div>
                    <div className="text-11 font-mono text-ink-faint">{s.expr}</div>
                  </td>
                  <td><span className="text-12 font-mono text-accent font-semibold">{s.nextRun}</span></td>
                  <td className="text-12 text-ink-faint">{s.lastRun}</td>
                  <td><StatusBadge status={s.status} /></td>
                  <td className="text-right">
                    <span className="inline-flex items-center gap-3">
                      <button className="k-btn-secondary" onClick={() => setDrawer({ open: true, schedule: s })}>Edit</button>
                      <Toggle checked={s.status === 'active'} onChange={(v) => toggle(s.id, v)} />
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {drawer.open && (
        <ScheduleDrawer
          schedule={drawer.schedule}
          onClose={() => setDrawer({ open: false, schedule: null })}
          onSaved={() => setReloadKey(k => k + 1)}
        />
      )}
    </div>
  )
}
